// Module `admin` (API-v3 11): overview and ToRs, logframe and readings,
// reports, budget/expenses/invoices, amendments, expert days, documents,
// contract and close. Owned by WP admin.
import { z } from "zod";
import { formatNumber } from "../money";
import {
  BUDGET_LINE_KINDS,
  type BudgetLineKind,
  ACTIVITY_TYPES,
  AMENDMENT_KINDS,
  FUNDING_TYPES,
  REPORT_FORMATS,
  REPORT_KINDS,
  type ReportKind,
  type ActivityType,
  type FundingType,
  type ReportFormat,
  CLAIMANT_TYPES,
  INDICATOR_AUTO_SOURCES,
  INDICATOR_KINDS,
  DOCUMENT_TYPES,
  INVOICE_KINDS,
  PAYMENT_METHODS,
  READING_METHODS,
  RETENTION_CHOICES,
  TOR_ACTIONS,
  TOR_FIELD_KINDS,
  type DocumentType,
  type IndicatorAutoSource,
  type IndicatorKind,
  type ReadingMethod,
  type TorFieldKind,
  type TorStatus,
} from "../enums";
import { CONTRACT_PRESETS_IDS, CURRENCY_RULES, DP_REGIMES, INVOICE_UNITS, type ContractPreset, type ContractProfile } from "../contract-profile";
import { LIMITS } from "../limits";
import { numberInputError, readNumber } from "../money";
import { httpUrlOrEmpty, uuid } from "../schemas/common";
import { baselineYear, putLogframeBody } from "../schemas/logframe";
import type { CostLine } from "../schemas/cost-lines";
import type { IndicatorBarState } from "../indicator-figures";
import type { MailStatus } from "./mail";
import { lineNumberName } from "./activities";
import {
  centsV3,
  country,
  days,
  isoDate,
  text,
  textOrEmpty,
  textOrNull,
  type FileRef,
  type FromRecord,
  type HistoryEntry,
  type Id,
  type Link,
  type Paged,
  type StatusChip,
  type Steps,
} from "./common";

export type { DocumentType, TorFieldKind, TorStatus };

// ---------- 11.1 overview and ToRs ----------

export const torStatusFilter = z.enum(["all", "draft", "signed_off", "submitted", "revision", "approved"]);
export const torField = z.object({
  key: z.string().trim().min(1).max(60),
  label: text(200, "Label"),
  kind: z.enum(TOR_FIELD_KINDS),
  value: z.union([textOrEmpty(LIMITS.blockText), z.array(textOrEmpty(2_000)).max(100)]),
  fromRecord: z.object({ text: z.string().max(500), link: z.object({ label: z.string().max(200), path: z.string().max(500) }).nullable() }).nullable(),
});
export type TorField = { key: string; label: string; kind: TorFieldKind; value: string | string[]; fromRecord: FromRecord | null };
export const torSection = z.object({ key: z.string().trim().min(1).max(60), title: text(LIMITS.sectionTitle, "Section title"), fields: z.array(torField).max(40) });
export type TorSection = { key: string; title: string; fields: TorField[] };

export type AdminOverview = {
  money: {
    envelopeCents: number | null;
    cofinancingPct: number | null;
    approvedCents: number;
    approvedTors: number;
    onHoldCents: number;
    onHoldTors: number;
    /** LF-N4: the ToRs not approved yet, by where they stand (onHoldTors = withFunder + signedOff + changesRequested; drafts apart). */
    notApproved: NotApprovedCounts;
    oldestDays: number | null;
    draftsCents: number;
    drafts: number;
    remainingCents: number | null;
  };
  torCounts: Record<"all" | "draft" | "signed_off" | "submitted" | "revision" | "approved", number>;
  tors: {
    id: Id;
    title: string;
    sub: string;
    code: string | null;
    dates: string;
    budgetCents: number;
    status: StatusChip;
    withFunder: string | null;
    path: string;
    /** The day it was sent or decided (null while it is a draft): the Approvals tab filters by it. */
    at?: string | null;
    /** "Decided by Elena Petrova in Convenor on 22 Sep 2026", or how the team recorded an answer that came by email. */
    decided?: string | null;
    /** Suite Approvals: what the funder said when asking for changes ("Please lower the venue budget to €12,000."). */
    funderNote?: string | null;
    /** AC5: an approved ToR's approval date and the funder's reference (an Ares number), null otherwise. */
    approval?: { on: string | null; reference: string | null; line: string } | null;
    /** AP1: a reminder can be sent (the ToR is with the funder and the user is the contract lead). */
    canRemind?: boolean;
    /** AP1: the last reminder sent on this ToR: the day, and what happened to the email. */
    reminder?: { sentOn: string; mail: MailStatus | null } | null;
  }[];
  /** How many days the funder usually takes to answer on a ToR ("Usually answered in 6 days"). */
  torDecisionDays?: number;
  reports: ReportRowV3[];
  indicators: { code: string; name: string; shortName: string | null; baseline: string; target: string; current: string; status: StatusChip }[];
  links: { funderView: string; close: string; editContract: string; newTor: string };
  isLead: boolean;
  /** Payment requests waiting: overdue, or expected within 30 days. */
  payments: { id: Id; title: string; amountCents: number; expectedOn: string | null; overdue: boolean; path: string }[];
  /** The report still being written that is due first: the Overview's main button "Open <title>". */
  nextReport?: { id: Id; title: string; path: string } | null;
  /**
   * The budget block (Suite Administration): Reported (spent inside a period
   * whose report the funder has), Approved, not yet reported (the rest of what
   * is spent), Waiting for approval (ToRs with the funder), Remaining. Always
   * the whole project; `period` is the picked period's spending and events.
   */
  budget?: {
    reportedCents: number;
    approvedNotReportedCents: number;
    /** HO3, BU1: what approved ToRs, open contracts and closed events' final figures still hold. */
    committedCents: number;
    waitingCents: number;
    /** BU-N2: waitingCents by where the ToRs stand. */
    waitingSplit: WaitingSplit;
    remainingCents: number | null;
    contractCents: number | null;
    cofinancingPct: number | null;
    /** Indirect costs as a share of direct costs, calculated (null = none set). */
    indirectPct: number | null;
    period: { spentCents: number; events: number; participants: number } | null;
  };
  /**
   * One row per result: its first indicator's progress with units ("3 of 4 events", "100% of 60%"), which
   * indicator that is (LF11) and how many more it has, and the worst status of its indicators.
   */
  results?: {
    id: Id;
    code: string;
    title: string;
    level: string;
    progress: string;
    progressPct: number;
    status: StatusChip;
    path: string;
    /** LF11: the indicator the progress is of ("1.1 Agencies sharing data"); null = the result has none. */
    indicator?: { code: string; label: string } | null;
    /** LF11: the result's other indicators, not in the progress. */
    otherIndicators?: number;
    /** Workbook 27 Oct: what the bar shows besides its length (no reading yet, below start, above target). */
    barState?: IndicatorBarState;
    /** LF11 (27 Oct): every indicator of the result with its own figure and bar, the first one included. */
    indicators?: { id: Id; code: string; label: string; progress: string; progressPct: number; barState: IndicatorBarState; status: StatusChip }[];
  }[];
  /** The next deadlines, soonest first (at most four). */
  deadlines?: { kind: "report" | "payment" | "steering" | "audit"; date: string; title: string; detail: string; path: string | null; /** A report past its due date and not sent. */ overdue?: boolean }[];
};
export type NewTorOptions = {
  activities: {
    id: Id;
    label: string;
    prefill: { title: string; startDate: string | null; endDate: string | null; location: string; participants: number | null };
    /** AD3: an (N)KE mission's ToR asks no participants. Left out for "None". */
    type?: "event" | "deliverable" | "nke";
  }[];
};
export const newTorBody = z.object({
  activityId: uuid.nullable(),
  title: text(LIMITS.torTitle, "Title"),
  paymentMethod: z.enum(PAYMENT_METHODS, { errorMap: () => ({ message: "Choose a payment method." }) }),
  // optional additions (New ToR wireframe): prefilled from the activity, editable
  location: textOrEmpty(LIMITS.location, "Location").optional(),
  startDate: isoDate.nullable().optional(),
  endDate: isoDate.nullable().optional(),
  participants: textOrEmpty(LIMITS.participants, "Participants").optional(),
});
export const torPatchV3 = z
  .object({
    title: text(LIMITS.torTitle, "Title").optional(),
    fields: z.array(torSection).max(LIMITS.sectionsMax).optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    location: textOrEmpty(LIMITS.location).optional(),
    saveAsOrgTemplate: z.boolean().optional(),
  })
  .strict();
export const torExpertsBody = z.object({
  experts: z.array(z.object({ engagementId: uuid, role: textOrEmpty(120), days: days() })).max(50),
});
export const torTransitionBody = z.object({
  action: z.enum(TOR_ACTIONS),
  note: textOrEmpty(LIMITS.transitionNote).optional(),
  approvalReference: textOrEmpty(120).optional(),
  approvedOn: isoDate.optional(),
  approvalFileId: uuid.nullable().optional(),
  /** submit: send although nobody has the contract manager view (the team emails the ToR itself). */
  withoutContractManager: z.boolean().optional(),
});
/** line: the full sentence (the ToR page); short (GL-F2, lists such as Approvals): one muted line, "Decided by Anne Moreau · 23 Oct". */
export type TorDecisionLine = { kind: "approved" | "changes_requested"; version: number; inConvenor: boolean; line: string; short: string };
export type TorRevision = {
  fromVersion: number;
  toVersion: number;
  funderSaid: string;
  /** Budget, cost kinds and facts, before and after; document sections that were rewritten. */
  changes: { label: string; before: string; after: string; changed: boolean }[];
};
export type ApprovalPreview = { versionLine: string; whatHappens: string[] };
export type TorDetailV3 = {
  id: Id;
  reference: string;
  title: string;
  status: StatusChip;
  statusKey: TorStatus;
  version: number;
  code: string | null;
  budgetCents: number;
  daysWithFunder: number | null;
  metaLine: string;
  steps: Steps;
  /** AC10: the funder's comments, with the email or letter they came in when one was attached. */
  funderComments: { version: number; text: string; file?: { name: string; url: string } | null } | null;
  /** AC3 (round 5): on an approved ToR, where its activity moved away from it since (dates, place, costs; event deadlines and sessions); empty when nothing. */
  drift?: string[];
  /** AC5 (round 5): the funder's approval: reference (e.g. an Ares number), date and letter; null until approved. */
  approval?: { reference: string | null; approvedOn: string | null; line: string; file: { name: string; url: string } | null } | null;
  /**
   * The funder's latest decision and where it came from: taken by the contract
   * manager in their own Convenor view (recorded automatically), or recorded by
   * the team from an answer that came by email.
   */
  decision: TorDecisionLine | null;
  /**
   * While a new version is prepared after the funder asked for changes: what
   * they asked, and what changes against the version they saw.
   */
  revision: TorRevision | null;
  activity: { id: Id; code: string; title: string; type: string; path: string } | null;
  sections: TorSection[];
  experts: { engagementId: Id; name: string; category: string; role: string; days: number; path: string; daysLeft: number; cvStatus: string }[];
  costLines: CostLine[];
  /** AC2, #20: the project's budget lines for the cost-line picker, the activity's own lines first (activity: true). */
  budgetLines?: { id: Id; label: string; activity: boolean }[];
  /** The line a new cost line goes on when none is picked: the activity's line. */
  defaultBudgetLineId?: Id | null;
  /** AC2 (workbook 27 Oct): that line per kind of cost (venue, per_diem, ...), for an item whose words say its kind. */
  defaultBudgetLineIds?: Partial<Record<string, Id | null>>;
  /** AC4: the activity's early estimate against the ToR total; null without an estimate. */
  estimate?: { cents: number; over: boolean; line: string } | null;
  history: HistoryEntry[];
  versions: { version: number; sentAt: string; sentBy: string; path: string }[];
  comments: { author: string; text: string; at: string }[];
  saveState: { updatedAt: string };
  /** AC-N1: who sends the ToR to the funder once signed off ("Laura Jansen"; drafts and signed-off ToRs); null otherwise. */
  sendsIt?: string | null;
  /** F2: the last ask to send this signed-off version ("You asked Laura Jansen on 30 Oct."); recent = within a day, so no new ask. */
  askedToSend?: { line: string; on: string; recent: boolean } | null;
  /** askToSend (AC-N1): a team member who cannot send it asks the contract lead to. newVersion (AC-N6): "Make version N+1" of an approved ToR. */
  can: { edit: boolean; signOff: boolean; submit: boolean; recordDecision: boolean; export: boolean; askToSend?: boolean; newVersion?: boolean };
  /** AC-N6: the activity's dates now, when they differ from the approved ToR's: what the new version starts with. */
  newVersionDates?: { startDate: string; endDate: string | null } | null;
  /** True when nobody has (or is invited to) the contract manager view: "Send to the funder" would email nobody. */
  contractManagerMissing?: boolean;
  pool: { engagementId: Id; name: string; category: string; expertise: string | null; daysLeft: number; cv: "current" | "old" | "missing" }[];
  /** optional addition: "Draft a reminder" to the funder contact while the ToR is with the funder */
  reminderMailto?: string | null;
};
export type TorExportPreview = {
  line: string;
  files: { file: string; content: string; source: string; status: StatusChip }[];
  warnings: string[];
  profiles: {
    name: string;
    category: string;
    role: string;
    expertise: string;
    languages: string[];
    nationality: string | null;
    daysInTor: string;
    feePerDay: string;
    earlierWork: string[];
  }[];
};
export const torExportBody = z.object({
  format: z.enum(["zip", "folders", "pdf", "word"]),
  include: z.object({ cv: z.boolean(), profileSheet: z.boolean(), declaration: z.boolean(), contact: z.boolean() }),
});
export type TorVersionV3 = {
  version: number;
  title: string;
  sentLine: string;
  sections: (TorSection & { changed: boolean })[];
  budget: { item: string; totalCents: number }[];
  totalCents: number;
};
export const torCommentBody = z.object({ text: text(LIMITS.comment, "Comment") });

// ---------- 11.2 logframe and readings ----------

export type LogframeView = {
  groups: {
    /** title: the full statement; shortName: a few plain words, null = none yet */
    /** levelNo 1 = the top level; levelName: the project's name for it ("" for "Other indicators") */
    /** parentId: the result above it (null = none); every result is listed, with or without indicators */
    /** alsoFeedsInto (LF1, 27 Oct): the other results it feeds into besides parentId. */
    result: { id: Id; code: string; title: string; shortName: string | null; levelNo: number | null; levelName: string; parentId?: Id | null; alsoFeedsInto?: Id[] };
    indicators: {
      id: Id;
      code: string;
      name: string;
      shortName: string | null;
      baseline: string;
      target: string;
      /** The goal as stored (the shown `target` is formatted, "8,000"): Change the goal starts from it. */
      targetValue?: number | null;
      current: string;
      verification: { fromRecord: FromRecord | null; file: FileRef | null; uploadPath: string };
      status: StatusChip;
      /** R3: count, share from answers or reading of the outside world; autoSource: the count's (or share's) automatic source */
      kind?: IndicatorKind;
      autoSource?: IndicatorAutoSource | null;
      /** R3: feedback questions that count towards a share, across the project's events */
      linkedQuestions?: number;
      /** LF6: each of those questions with its event ("Workshop Skopje", "Did your view change?"). */
      questionLinks?: { event: string; question: string; path: string | null }[];
      /** LF6: a share from feedback with fewer answers is flagged; LF17: shown as % or as people. */
      minAnswers?: number;
      shareAs?: "pct" | "people";
      /** LF7: "Reported as FPI 8b · Number of people trained…" (the funder indicator's name when it is on the list). */
      funderLine?: string | null;
      /** optional addition: the "Source of verification" wording from the logframe */
      means?: string;
      /** optional addition: the latest reading, for confirming it */
      latestReading?: {
        id: Id;
        date: string;
        value: number;
        confirmed: boolean;
        /** "Counted from our records", "Outside source: Eurostat"; null = not recorded */
        how?: string | null;
        /** the event or deliverable the reading came from: "from Workshop Skopje" */
        from?: { text: string; path: string | null } | null;
        /** R3: a link to the proof, when the reading has one */
        evidenceUrl?: string | null;
        /** LF6: made from feedback answers and kept up to date by itself. */
        fromFeedback?: boolean;
      } | null;
      /** Suite Logframe: "0", "350 · 45% women", "229 · 38% women" (with the women's share when broken down by gender). */
      start?: string;
      goal?: string;
      now?: string;
      /** How far now is from start to goal, 0 to 100 (the bar). */
      progressPct?: number;
      /** S-038: the same without the cap at 100 (150: half as far again past the goal), so the bar can mark the target; null = no value or target. */
      reachPct?: number | null;
      /** Workbook 27 Oct: no reading yet (dashed track), below start, above target, or plain progress. */
      barState?: IndicatorBarState;
      /** LF4: the value now is ten times above or below the baseline (6.45 against 6,122): probably a slip to correct. */
      farFromBaseline?: boolean;
      /** LF6 (27 Oct): a reading typed by hand on a share from feedback, overruled by the answers' readings; null = none. */
      overruledReading?: { id: Id; date: string; value: number } | null;
      /** "Count from records · events in Convenor": the kind and the source of verification. */
      howLine?: string;
      /** The funder-wide indicator it is linked to ("FPI 27"); "" = none. */
      funderCode?: string;
      breakdown?: "none" | "gender";
      targetDate?: string | null;
      /** Every reading in the period, newest first (the indicator panel). */
      readings?: { id: Id; date: string; value: number; confirmed: boolean; how: string | null; fromFeedback?: boolean }[];
      /** LF2: the baseline as stored (the shown `baseline` is formatted); its year and the baseline in words ("" = none). */
      baselineValue?: number | null;
      /** The indicator's unit ("people", "%"); "" = none. */
      unit?: string;
      baselineYear?: number | null;
      baselineNote?: string;
      /** LF3: every target, in order; the final one is mirrored in targetValue/targetDate. */
      targets?: IndicatorTargetView[];
    }[];
    /** Suite Logframe "Work that feeds this": the lines of work serving the result, "3 of 5" activities done of planned. */
    work?: { id: Id; name: string; done: number; planned: number; path: string; /** LF17: null = the planned number is not set */ plannedCount?: number | null; code?: string }[];
  }[];
  /** The project's result levels, top first (2 to 5), with how many results each has. */
  levels: { no: number; name: string; results: number }[];
  canEdit: boolean;
  /** What the results rest on; at risk ones come first. */
  assumptions?: AssumptionRow[];
  /** The funder's required checks for this kind of action. */
  checks?: RequiredCheckRow[];
};
/** LF7: GET /logframe/funder-indicators, project indicators grouped by the funder indicator they are reported as. */
export type FunderRollup = {
  groups: {
    /** "FPI 8b", as on the list, or as typed when it is not on it */
    code: string;
    /** the list's name; null when typed */
    name: string | null;
    indicators: { id: Id; code: string; label: string; result: string; current: string; value: number | null; date: string | null; confirmed: boolean }[];
  }[];
};
/**
 * resultId: the first linked result (older readers); results: every linked
 * result with its code (LF10). mitigation and reviewDate: LF15, for an
 * assumption at risk ("" and null = not given).
 */
export type AssumptionRow = {
  id: Id;
  resultId: Id | null;
  text: string;
  atRisk: boolean;
  note: string;
  resultIds?: Id[];
  results?: { id: Id; code: string; label: string }[];
  mitigation?: string;
  reviewDate?: string | null;
  /** LF10 (27 Oct): how many other assumptions have the same words (stored twice before one could serve several results); 0 = none. */
  sameWords?: number;
};
/** LF10: two assumptions with the same words are one: lower case, spaces and a final full stop do not count. */
export const assumptionKey = (text: string) => text.trim().replace(/\s+/g, " ").replace(/[.\s]+$/, "").toLowerCase();
/** LF3: a target as shown and edited. breakdownProblem: the parts do not add up (a warning), else null. */
export type IndicatorTargetView = {
  id: Id;
  value: number | null;
  date: string | null;
  label: string;
  note: string;
  unit: string;
  breakdownBy: string;
  parts: { label: string; value: number }[];
  final: boolean;
  breakdownProblem: string | null;
};
export type RequiredCheckRow = { id: Id; text: string; done: boolean; /** "Uploaded 12 Mar 2026", "Due with report 2" */ line: string; file: FileRef | null; status: StatusChip };
export const newAssumptionBody = z.object({
  kind: z.enum(["assumption", "check"]),
  text: text(500, "Text"),
  resultId: uuid.nullable().default(null),
  /** LF10: the results an assumption belongs to (resultId, when given, is added to them). */
  resultIds: z.array(uuid).max(20).default([]),
  note: textOrEmpty(1000).default(""),
  dueNote: textOrEmpty(200).default(""),
  /** LF15: what the team does about it, and when it looks again. */
  mitigation: textOrEmpty(1000, "What we do about it").default(""),
  reviewDate: isoDate.nullable().default(null),
});
export const assumptionPatch = z
  .object({
    text: text(500, "Text").optional(),
    /** An assumption: ok or at_risk; a check: open or done. */
    status: z.enum(["ok", "at_risk", "open", "done"]).optional(),
    note: textOrEmpty(1000).optional(),
    dueNote: textOrEmpty(200).optional(),
    fileId: uuid.nullable().optional(),
    /** LF10: replaces the linked results ([] = the whole project). */
    resultIds: z.array(uuid).max(20).optional(),
    /** LF15 */
    mitigation: textOrEmpty(1000, "What we do about it").optional(),
    reviewDate: isoDate.nullable().optional(),
  })
  .strict();
/** PUT /logframe keeps the v2 structure body. */
export const logframeStructureBody = putLogframeBody;
/** proposal.method: how a reading taken from the proposal was obtained (the form preselects it). */
/** indicators[].kind (R3): the form asks for proof when the indicator is a reading of the outside world. */
/** indicators[].baseline (LF4): the form warns when a reading is far from it (a typing slip such as 6.45 for 6450). */
/** LF6 (27 Oct): fromAnswers = a share from feedback whose readings the answers make (a reading typed by hand does not count). */
export type ReadingOptions = { indicators: { id: Id; label: string; kind?: IndicatorKind; baseline?: number | null; fromAnswers?: boolean }[]; proposal: { value: number; explanation: string; method: ReadingMethod } | null };
const VALUE_MESSAGE = "Enter a number, with at most two decimals.";
/**
 * LF4: a reading's value, as a number or as the text the person typed. Text
 * goes through `readNumber`, the reader of the budget import: "6 450" and
 * "1.234,5" read fine, the ambiguous "6,450" is refused with a message that
 * names both meanings. Two decimals at most (the stored precision).
 */
export const readingValue = z
  .union([z.number(), z.string()])
  .transform((v, ctx) => {
    const n = typeof v === "number" ? v : readNumber(v);
    if (n === null || !Number.isFinite(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: (typeof v === "string" && numberInputError(v)) || VALUE_MESSAGE });
      return z.NEVER;
    }
    return n;
  })
  .pipe(
    z
      .number()
      .min(-LIMITS.indicatorValueAbsMax)
      .max(LIMITS.indicatorValueAbsMax)
      .refine((n) => Number.isInteger(Number((n * 100).toPrecision(15))), VALUE_MESSAGE),
  );
export const newReadingBody = z
  .object({
    date: isoDate,
    value: readingValue,
    note: textOrEmpty(LIMITS.readingNote),
    fileId: uuid.nullable(),
    confirm: z.boolean(),
    /** How the value was obtained; null = not recorded. */
    method: z.enum(READING_METHODS).nullable().default(null),
    /** Required when method = outside_source; ignored otherwise. */
    sourceName: textOrNull(LIMITS.readingSourceName, "Source").default(null),
    /** R3: a link to the proof (a web page), besides or instead of the uploaded file. */
    evidenceUrl: httpUrlOrEmpty(500).default(""),
  })
  .superRefine((b, ctx) => {
    if (b.method === "outside_source" && !b.sourceName) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sourceName"], message: "Name the outside source." });
  });
/** PATCH /logframe/results/:id and /logframe/indicators/:id: the short name ("" or null clears it). */
export const logframeShortNameBody = z.object({ shortName: textOrNull(LIMITS.logframeShortName, "Short name") });
/**
 * PATCH /logframe/results/:id: the short name and (LF1) the result it feeds
 * into, exactly one level up (null = none, only at the top level or when that
 * level has no results). Left out = unchanged.
 */
export const logframeResultPatch = z
  .object({
    shortName: textOrNull(LIMITS.logframeShortName, "Short name").optional(),
    parentId: uuid.nullable().optional(),
    /** LF1 (27 Oct): every result it feeds into, one level up; the first is the main one (parentId). Wins over parentId. */
    parentIds: z.array(uuid).max(20).optional(),
  })
  .strict();
/**
 * PATCH /logframe/indicators/:id: the short name and (R3) the kind with its
 * automatic source. A kind without a source clears a source that does not fit.
 */
export const indicatorPatchV3 = z
  .object({
    shortName: textOrNull(LIMITS.logframeShortName, "Short name").optional(),
    /** Suite Logframe: the funder-wide indicator code ("FPI 27") and the breakdown of readings. */
    funderCode: textOrEmpty(40).optional(),
    breakdown: z.enum(["none", "gender"]).optional(),
    /** Changing the goal makes a new version of the results framework (Suite Logframe indicator panel). */
    target: z.number().min(-1e12).max(1e12).nullable().optional(),
    /** LF2: the baseline (a number, optional), its year and the baseline in words; a change makes a new version. */
    baseline: z.number().min(-1e12).max(1e12).nullable().optional(),
    baselineYear: baselineYear.nullable().optional(),
    baselineNote: textOrEmpty(1000, "Baseline note").optional(),
    targetDate: isoDate.nullable().optional(),
    kind: z.enum(INDICATOR_KINDS).optional(),
    autoSource: z.enum(INDICATOR_AUTO_SOURCES).nullable().optional(),
    /** LF6: below this many answers a share from feedback is flagged and its readings stay unconfirmed. */
    minAnswers: z.number().int().min(1, "Enter at least 1.").max(1000, "Enter 1000 or fewer.").optional(),
    /** LF17: a share from feedback shown as a percentage or as the number of people. */
    shareAs: z.enum(["pct", "people"]).optional(),
  })
  .strict();
/**
 * PATCH /logframe/readings/:id. LF5: correcting the value or the date needs a
 * reason; the old and new value and the reason go to the indicator's History.
 */
export const readingPatchV3 = z
  .object({
    confirmed: z.boolean().optional(),
    fileId: uuid.nullable().optional(),
    note: textOrEmpty(LIMITS.readingNote).optional(),
    value: readingValue.optional(),
    date: isoDate.optional(),
    reason: textOrEmpty(LIMITS.reasonText, "The reason").optional(),
  })
  .superRefine((b, ctx) => {
    if ((b.value !== undefined || b.date !== undefined) && !b.reason) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["reason"], message: "Say why the reading is corrected." });
  });
/** POST /logframe/readings/:id/withdraw (LF5): the reading leaves the figures and exports; the reason stays in History. */
export const readingWithdrawBody = z.object({ reason: text(LIMITS.reasonText, "The reason") });

// ---------- Edit results (Suite Edit Results) ----------

/** The whole results framework for editing: level names, results in order, lines of work. */
export type ResultsFramework = {
  /** The version in use now (1 = as imported); saving makes `version + 1`. */
  version: number;
  levelNames: string[];
  results: { id: Id; parentId: Id | null; levelNo: number; code: string; statement: string; shortName: string | null; indicators: number; lineOfWorkIds: Id[]; /** What points to the result: it cannot be removed while any is above 0. */ usedBy: { activities: number; tors: number; expertNeeds: number } }[];
  /** code: LF17, from the logframe ("A1.2"); "" = none. */
  linesOfWork: { id: Id; name: string; code?: string; kind: ActivityType; plannedCount: number | null; activities: number }[];
  versions: { version: number; note: string; by: string; at: string }[];
  canEdit: boolean;
};
/** "2 activities, 1 ToR and 1 expert need": what uses a result (Edit results). */
export function resultUsedByLine(u: ResultsFramework["results"][number]["usedBy"]): string {
  const parts = [
    u.activities ? `${u.activities} ${u.activities === 1 ? "activity" : "activities"}` : null,
    u.tors ? `${u.tors} ${u.tors === 1 ? "ToR" : "ToRs"}` : null,
    u.expertNeeds ? `${u.expertNeeds} expert ${u.expertNeeds === 1 ? "need" : "needs"}` : null,
  ].filter((x): x is string => x !== null);
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : (parts[0] ?? "nothing");
}
export const resultInUse = (u: ResultsFramework["results"][number]["usedBy"]) => u.activities + u.tors + u.expertNeeds > 0;
export const resultsFrameworkBody = z.object({
  levelNames: z.array(text(40, "Level name")).min(2).max(5),
  /** In order, top-down: each result's parent is the nearest result above it on a higher level. */
  results: z
    .array(
      z.object({
        id: uuid,
        levelNo: z.number().int().min(1).max(5),
        code: textOrEmpty(16),
        statement: text(1000, "Statement"),
        shortName: textOrNull(LIMITS.logframeShortName, "Short name"),
        /**
         * LF1: the result it feeds into, one level up, in this body. Left out
         * (older screens) = the nearest result above it on a higher level.
         */
        parentId: uuid.nullable().optional(),
        lineOfWorkIds: z.array(uuid).max(50),
      }),
    )
    .max(200),
  linesOfWork: z.array(z.object({ id: uuid, name: text(200, "Name"), code: textOrEmpty(16, "Code").optional(), kind: z.enum(ACTIVITY_TYPES), plannedCount: z.number().int().min(0).max(100_000).nullable() })).max(100),
});
export type ResultsFrameworkBody = z.infer<typeof resultsFrameworkBody>;

// ---------- 11.3 reports ----------

export type ReportRowV3 = {
  id: Id;
  title: string;
  kind: string;
  period: string;
  due: string | null;
  status: StatusChip;
  path: string;
  /** the report's status (draft, submitted, approved, approved_with_comments, changes_requested) */
  statusKey?: string;
  /** The day the report was sent or the answer recorded (null for a draft). */
  at?: string | null;
  /** "Answer recorded on 28 May 2026 from the funder's email or letter" once the funder answered. */
  decided?: string | null;
  /** Suite Reports, a report still being written: how ready its parts are (results, budget, evidence, sections) and its blockers. */
  readiness?: {
    results: { ready: number; of: number };
    budgetReady: boolean;
    evidence: { ready: number; of: number };
    sections: { ready: number; of: number };
    blockers: number;
    /** 0 to 100, for the bar. */
    pct: number;
  } | null;
  /** Each blocker with what it is about and where to fix it. */
  blockerItems?: { title: string; detail: string; path: string; action: string }[];
  /** A report sent: "Submitted 12 Apr", "Accepted 3 May", "Payment 2 received". */
  timeline?: string[];
};
export type ReportsView = {
  rows: ReportRowV3[];
  calendar: { due: string; what: string; reportId: Id | null; /** optional additions (calendar editor) */ id?: Id; kind?: ReportKind; startDate?: string; endDate?: string }[];
  next: { label: string; periodId: Id } | null;
  /** Someone set or confirmed the calendar; false while it is only the one suggested from the contract dates. */
  calendarConfirmed: boolean;
};
export const newReportBody = z.object({ periodId: uuid });
export const reportingCalendarBody = z.object({
  periods: z
    .array(z.object({ id: uuid.optional(), kind: z.enum(REPORT_KINDS), startDate: isoDate, endDate: isoDate, dueDate: isoDate }))
    .max(20),
});
export type BundleItem = {
  key: "narrative" | "financial" | "logframe" | "timesheets" | "attendance" | "invoice" | "visibility" | "evaluation";
  label: string;
  detail: string;
  state: StatusChip;
  path: string | null;
};
export type ReportDetailV3 = {
  id: Id;
  title: string;
  status: StatusChip;
  statusKey: string;
  line: string;
  blockers: string[];
  activities: { code: string; activity: string; path: string; account: StatusChip; evidence: { text: string; missing: boolean }; fixes: Link[] }[];
  /** name: the full name; shortName: a few plain words, null = none yet */
  indicators: { code: string; name: string; shortName: string | null; target: string; current: string; reading: StatusChip }[];
  bundle: BundleItem[];
  submittedAt: string | null;
  clock: { until: string; daysLeft: number } | null;
  decision: { decision: string; comments: string; file: FileRef | null; decidedOn: string; questionsDueOn?: string | null } | null;
  can: { write: boolean; submit: boolean; decide: boolean };
  sendDisabledReason: string | null;
  history: HistoryEntry[];
  /** When the report was sent and its figures (financial report, indicators) were frozen; null while they follow the records. */
  figuresFrozenAt?: string | null;
  /** The financial report of the report's period. */
  financialPath?: string;
  /** "interim" or "final" (the final report covers the whole project). */
  kind?: "interim" | "final";
  /** What's in this report: everything of its period, with links (review V2, the cheap step). */
  contents?: ReportContentsRow[];
  /** The final report only: one section per result, the reports sent, the final evaluation. */
  final?: FinalReportView | null;
  /**
   * Suite Report Response: the payment this report releases. "Interim payment 2 released on 28 May 2026"
   * (received), asked, drafted; or, once accepted without one, where to ask for it.
   */
  payment?: { line: string; path: string; done: boolean } | null;
};

/** One line of "What's in this report". */
export type ReportContentsRow = {
  key: "activities" | "readings" | "costs" | "evidence" | "engagement" | "amendments";
  label: string;
  /** "12 activities, 8 done" */
  detail: string;
  path: string | null;
  state: StatusChip;
  /** Activities by line of work, readings by indicator, meetings, amendments. */
  groups: { title: string | null; path: string | null; items: { label: string; sub: string; path: string | null; status: StatusChip | null }[] }[];
};

/** One result in the final report (notes/results-model.md §6). */
export type FinalReportResult = {
  id: Id;
  code: string;
  statement: string;
  shortName: string | null;
  /** The project's name for its level ("Outcome"). */
  level: string;
  /** start = baseline, goal = target, now = the final value. */
  indicators: { code: string; name: string; shortName: string | null; start: string; goal: string; now: string; reading: StatusChip }[];
  /** The activities serving this result or a result below it, each once. */
  work: { count: number; done: number; items: { code: string; title: string; path: string; status: StatusChip }[] };
  proof: { withProof: number; of: number; items: { code: string; text: string; missing: boolean }[] };
};
export type FinalReportView = {
  results: FinalReportResult[];
  /** Activities serving any result, each counted once; and those serving none. */
  totals: { activities: number; done: number; unlinked: number };
  /** The assumptions of the logframe (at risk first): status, the results they belong to, what is done about them and when they are looked at again. */
  assumptions: { text: string; results: string; status: StatusChip; mitigation: string; reviewDate: string | null }[];
  /** Said when no assumption is recorded; empty otherwise. */
  assumptionsNote: string;
  /** The other reports of the project, in order. */
  reports: { id: Id; title: string; period: string; status: StatusChip; path: string }[];
  evaluation: { files: FileRef[]; uploadPath: string | null };
  closePath: string;
};
// ---------- the financial report (Suite Financial Report) ----------

/** One budget line in the financial report: the period and everything up to its end. */
export type FinancialLine = {
  id: Id;
  number: number;
  name: string;
  /** The funder's code, quiet detail. */
  code: string | null;
  heading: string | null;
  budgetCents: number;
  /** Spent with a date inside the period. */
  periodCents: number;
  /** Spent with a date up to the end of the period (from the start of the project). */
  toDateCents: number;
  /**
   * Committed and not spent yet, as on the Budget tab (budgetFigures): open contracts and orders not yet
   * invoiced, approved ToRs. The figure of the day, frozen with the report when it is sent.
   * Missing in figures frozen before it existed. In figures frozen before waitingCents existed, it
   * also holds what was waiting for approval.
   */
  committedCents?: number;
  /** BU-N7: what ToRs signed off and not yet approved hold, apart from committed (as on the Budget tab). Missing in figures frozen before it existed. */
  waitingCents?: number;
  /** Budget minus spent to date, committed and waiting for approval. */
  remainingCents: number;
  /** Spent to date as a share of the budget. */
  usedPct: number;
  /** Units (B1); null when the line has no unit. */
  unit: string | null;
  plannedUnits: number | null;
  unitValueCents: number | null;
  periodUnits: number | null;
  usedUnits: number | null;
  /** Average price of a unit used up to the end of the period. */
  averageUnitCents: number | null;
  /** "92% used", "Overspent by €1,200", "Average €640 a flight, planned €600". */
  warnings: string[];
};
/** The id of the row for costs charged to no budget line (in the "Not on a budget line" group). */
export const NO_LINE_ID = "no-line";
/** "3 · Travel" (D15: lineNumberName), or "Not on a budget line". */
export const financialLineLabel = (l: Pick<FinancialLine, "id" | "number" | "name">): string => (l.id === NO_LINE_ID ? l.name : lineNumberName({ number: l.number, name: l.name }));
export type FinancialTotals = { budgetCents: number; periodCents: number; toDateCents: number; committedCents?: number; waitingCents?: number; remainingCents: number; usedPct: number };
export type FinancialExpense = {
  id: Id;
  date: string;
  lineNumber: number | null;
  lineName: string | null;
  what: string;
  from: string;
  amountCents: number;
  /** Receipts, invoices or signed timesheets on file for the cost. */
  documents: number;
  path: string;
  /** A late cost: dated in an earlier period whose report was already sent, approved after it (counted in this period). */
  late?: boolean;
  /** S-051: where the cost stands; every cost in the report is spent ("Spent, approved", "Spent, paid", "Spent, from a signed timesheet"). */
  status?: string;
  /** S-050: who approved it and when (a fee from a signed timesheet: who approved the timesheet, else the expert who signed it). */
  approvedBy?: string | null;
  approvedOn?: string | null;
};
/**
 * D8 (Simon S-051): what is promised on a line and not spent yet, per activity: approved ToRs, contracts and orders
 * not yet invoiced (committed), and ToRs signed off but not approved yet (waiting for approval). The same rows as the
 * opened budget line's "What is committed"; they add up to the line's committed and waiting.
 */
export type FinancialCommitment = { lineNumber: number | null; lineName: string | null; what: string; holds: string; committedCents: number; waitingCents: number };
/** The figures of a financial report. Frozen with the report when it is sent (report.figures). */
export type FinancialFigures = {
  from: string;
  to: string;
  /**
   * Lines grouped by the funder's heading, in budget order. Lines without a heading: "Other lines" (or "Budget lines" when no line has one).
   * Then, when there are any, two more groups so the totals add up: "Late costs from earlier periods" (kind "late": costs dated in
   * an earlier period whose report was already sent, approved after it; counted in this period, their budget and to-date figures
   * stay on their own line) and "Not on a budget line" (kind "none": spent costs charged to no budget line).
   */
  headings: { heading: string; kind?: "late" | "none"; lines: FinancialLine[]; totals: FinancialTotals }[];
  totals: FinancialTotals;
  /** Payments the funder made (payment requests received): in the period and up to its end. */
  moneyIn: { periodCents: number; toDateCents: number; payments: number };
  /** Costs of the period: how many, how many without a document, and how many are still to approve (not counted). */
  costs: { count: number; withoutDocument: number; toApprove: number };
  expenses: FinancialExpense[];
  /** D8: the commitments on the day (frozen with the report); figures frozen before 1 Nov 2026 have none. */
  commitments?: FinancialCommitment[];
};
export type FinancialReportView = {
  /** Reporting periods, then "whole" (the whole project). */
  periods: { value: string; label: string }[];
  period: { value: string; label: string; from: string; to: string };
  figures: FinancialFigures;
  /** Set when the figures are the ones frozen when the report was sent. */
  frozen: { at: string; line: string } | null;
  /** The report of this period, if it was started. */
  report: { id: Id; title: string; path: string; status: StatusChip; readyAt: string | null } | null;
  can: { markReady: boolean };
  /** Suite Financial Report: the indirect costs of the period and to date, a share of the direct costs (null = none set). */
  indirect?: { pct: number; periodCents: number; toDateCents: number } | null;
  /** Each heading: Ready, or Check when a cost has no document or a line has a warning. */
  byHeading?: { heading: string; periodCents: number; lines: number; withoutDocument: number; warnings: number; status: StatusChip }[];
  /** "Use for payment request": a new payment request with this period's report. */
  paymentPath?: string | null;
};
export const financialReportQuery = z.object({ period: z.string().max(60).optional() });
/** The Administration period picker: the reporting periods and the whole project, and the one to open on. */
export type AdminPeriods = {
  /** Reporting periods in calendar order, then "whole" (the whole project). */
  periods: { value: string; label: string; from: string; to: string }[];
  /** The period being reported on now (its value), or "whole" when there is no reporting calendar. */
  current: string;
};
/** `?period=` on the overview, results and budget: a reporting period's id, or "whole" / "all". Left out = no filter. */
export const adminPeriodQuery = z.object({ period: z.string().max(60).optional() });
/** What a report keeps when it is sent: its financial figures and the indicator figures shown on it. */
export type ReportFigures = {
  frozenAt: string;
  financial: FinancialFigures;
  indicators: ReportDetailV3["indicators"];
  /** The final report: its results sections as sent. */
  results?: Pick<FinalReportView, "results" | "totals">;
  /** Report builder (board 1): the live cards in the text, frozen as sent, by their ref. */
  cards?: Record<string, import("./report-write").ReportCard>;
};

export const REPORT_SECTION_KEYS = ["summary", "context", "activities", "results", "partners", "difficulties", "visibility", "next_period"] as const;
/** The final report: "Lessons and sustainability" in place of "Next period". */
export const FINAL_REPORT_SECTION_KEYS = ["summary", "context", "activities", "results", "partners", "difficulties", "visibility", "lessons"] as const;
/** Every section key any template uses (report builder, board 1: the EU Annex VI template adds reach, voices, money and annexes). */
export const ALL_REPORT_SECTION_KEYS = [...REPORT_SECTION_KEYS, "lessons", "reach", "voices", "money", "annexes"] as const;
export type ReportSectionKey = (typeof ALL_REPORT_SECTION_KEYS)[number];
export type ReportSections = {
  saveState: { updatedAt: string };
  sections: {
    key: ReportSectionKey;
    number: number;
    title: string;
    state: "drafted" | "empty";
    note: string;
    text: string;
  }[];
  accounts: { activityId: Id; code: string; title: string; fromRecord: string; text: string; state: "drafted" | "empty" }[];
};
export const reportSectionsPatch = z.object({
  sections: z.array(z.object({ key: z.enum(ALL_REPORT_SECTION_KEYS), text: textOrEmpty(LIMITS.blockText) })).max(20).optional(),
  accounts: z.array(z.object({ activityId: uuid, text: textOrEmpty(LIMITS.blockText) })).max(500).optional(),
});
export const redraftBody = z.union([z.object({ key: z.enum(ALL_REPORT_SECTION_KEYS) }), z.object({ activityId: uuid })]);
/**
 * Suite Report: the report put together section by section on one page. The
 * wireframe's seven sections (About the action, Summary, Results and
 * activities, Partners and cooperation, Visibility, Changes, risks and
 * lessons, Plans for the next period), each with the facts and proof from the
 * records, its blockers with where to fix them, the team's text, whether it
 * is ready, and comments.
 */
export type ReportWorkbench = {
  id: Id;
  title: string;
  /** "Mar to Aug 2026 · due 14 Oct · funder template: EU narrative report (Annex VI) · the financial report is prepared in Budget" */
  line: string;
  template: string;
  sections: {
    key: ReportSectionKey;
    title: string;
    intro: string;
    state: StatusChip;
    ready: boolean;
    facts: { title: string; line: string; items: { label: string; path: string | null }[]; more: number; proof: string | null }[];
    blockers: { text: string; fix: { label: string; path: string } | null }[];
    text: string;
    comments: { id: Id; author: string; at: string; text: string }[];
  }[];
  ready: number;
  total: number;
  reviewers: { name: string; role: string }[];
  can: { write: boolean; submit: boolean };
  previewPath: string;
  submitPath: string;
  /** Suite Report "Before you submit": what must be done before the report goes to the funder. */
  checks: { label: string; done: boolean }[];
  submitNote: string;
  /** When the report went to the funder; null while it can still change. A sent report has no checks and no blockers: it shows as it was sent. */
  sentOn: string | null;
  /** RE1: what is still open ("6 of 7 sections not ready", "8 blockers open"). While any is, Sign off is off; signing off anyway needs a reason. */
  signOffOpen?: string[];
  /**
   * RE1 (27 Oct): a sign-off that no longer holds because more sections or
   * blockers are open than when the lead signed off ("The sign-off of 20 Oct
   * no longer holds: 5 blockers open"); null = none, or it still holds.
   */
  signOffLapsed?: string | null;
};
/** Suite Final Report: what the final report brings together, each part with where it stands. */
export type FinalOverview = {
  report: { id: Id; title: string; path: string; status: StatusChip } | null;
  /** The final period of the calendar (to start the report). */
  periodId: Id | null;
  due: string | null;
  parts: { key: "results" | "periods" | "evaluation" | "financial" | "lessons"; label: string; detail: string; status: StatusChip; path: string | null }[];
  canStart: boolean;
};
export const sectionReadyBody = z.object({ ready: z.boolean() });
/** POST /reports/:id/sign-off (RE1). No body while nothing is open; with sections or blockers open, the reason for signing off anyway (kept in the report's history). */
export const reportSignOffBody = z.object({ reason: textOrEmpty(LIMITS.reasonText, "The reason").default("") });
export const reportCommentBody = z.object({ section: z.enum(ALL_REPORT_SECTION_KEYS), text: text(2_000, "Comment") });
export type BundleView = { items: BundleItem[]; allReady: boolean; programmeManagerEmail: string | null };
export const submitReportBody = z.object({ via: z.enum(["download", "email"]) });
export const reportDecisionBody = z.object({
  decision: z.enum(["approved", "approved_with_comments", "changes_requested"]),
  comments: textOrEmpty(10_000),
  fileId: uuid.nullable(),
  decidedOn: isoDate,
  /** "Accepted with questions": the date the answers are due. */
  questionsDueOn: isoDate.nullable().optional(),
});

// ---------- 11.4 budget, expenses, invoices ----------

/**
 * The one name for the money ToRs signed off and not yet approved hold
 * (moneyBlock's waitingCents): with the funder, signed off but not sent, or
 * sent back for changes. On Home, the Administration overview, the Budget tab
 * and the budget export alike. Not "on hold", not "pending".
 */
export const WAITING_LABEL = "Waiting for approval";

/**
 * BU-N2 (Marius, 30 Oct): what is waiting for approval, by where its ToR
 * stands. The three add up to the waiting amount (projectMoney's
 * totals.waitingSplit); Home, the Budget tab and the Administration overview
 * all show them with waitingSplitText.
 */
export type WaitingSplit = { withFunderCents: number; signedOffCents: number; changesRequestedCents: number };

/** The part of WaitingSplit a ToR in this status counts in: sent (with the funder), signed off (not sent), sent back for changes. */
export const WAITING_STAGE: Partial<Record<string, keyof WaitingSplit>> = { submitted: "withFunderCents", signed_off: "signedOffCents", revision: "changesRequestedCents" };

export const noWaiting = (): WaitingSplit => ({ withFunderCents: 0, signedOffCents: 0, changesRequestedCents: 0 });

const WAITING_SPLIT_WORDS: [keyof WaitingSplit, string][] = [
  ["withFunderCents", "with the funder"],
  ["signedOffCents", "signed off, not sent"],
  ["changesRequestedCents", "changes requested"],
];

/** LF-N4: how many ToRs are not approved yet, by where they stand (the same stages as WaitingSplit, and the drafts). */
export type NotApprovedCounts = { withFunder: number; signedOff: number; changesRequested: number; drafts: number };

/** LF-N4: "1 with the funder · 1 signed off, not sent · 1 changes requested · 1 draft": the stages above zero, in WaitingSplit's words. */
export function notApprovedText(c: NotApprovedCounts): string {
  const parts: [number, string][] = [
    [c.withFunder, "with the funder"],
    [c.signedOff, "signed off, not sent"],
    [c.changesRequested, "changes requested"],
    [c.drafts, c.drafts === 1 ? "draft" : "drafts"],
  ];
  return parts.filter(([n]) => n > 0).map(([n, words]) => `${n} ${words}`).join(" · ");
}

/** "€41,540 with the funder · €31,820 signed off, not sent · €8,120 changes requested": the parts above zero. */
export function waitingSplitText(s: WaitingSplit | null | undefined, money: (cents: number) => string): string {
  const parts = WAITING_SPLIT_WORDS.filter(([k]) => (s?.[k] ?? 0) > 0).map(([k, words]) => `${money(s![k])} ${words}`);
  return parts.length ? parts.join(" · ") : "ToRs signed off, not approved yet";
}

/**
 * UI-BU1 (Marius's Budget wireframe), B1 and B5: where a line is expected to end. method "units": spent plus the planned
 * units still to use at what a unit has cost so far (D3: the unit itself and the line's other costs per unit, see byUnits); "pace": spent so far carried on to the end at the same pace;
 * "planned": too little to go on (no units used yet and fewer than 3 costs or 3 months), so the line is taken to be
 * used as budgeted. Never below spent plus committed and waiting. stillToPay: what the planned work will still cost
 * beyond spent, committed and waiting; notNeeded: the budget the planned work will not need.
 */
/**
 * D3 guard (decided on Edgar's behalf, 2 Nov 2026): a forecast by units carries the line's other costs (hotels,
 * per-diems) on per unit only once at least this many units are used, and at least this share of the planned units.
 */
export const OTHER_COSTS_MIN_UNITS = 5;
export const OTHER_COSTS_MIN_SHARE = 0.1;
export type LineOutlook = {
  method: "units" | "pace" | "planned";
  endCents: number;
  pct: number;
  stillToPayCents: number;
  notNeededCents: number;
  /**
   * D3 (Simon S-090): how a forecast by units was made. The units still planned (remainingUnits) at the
   * average cost of a unit so far (averageUnitCents, a flight), plus the line's other costs (hotels,
   * per-diems: otherCents so far) at their average per used unit (otherPerUnitCents); stillCents is what that comes to.
   * otherCarried: false when too few units are used to tell (at least 5 and 10% of the planned units): the other
   * costs so far are counted in spent, but not carried on (otherPerUnitCents is then 0).
   */
  byUnits?: { usedUnits: number; remainingUnits: number; averageUnitCents: number; otherPerUnitCents: number; otherCents: number; otherCarried: boolean; stillCents: number };
};
/** B2: the spending pace. lastsMonths/untilMonth: how long the money not yet spent lasts at that pace (null when nothing is spent yet). */
export type SpendingPace = { monthlyCents: number; months: number; periodMonthlyCents: number | null; periodMonths: number | null; lastsMonths: number | null; untilMonth: string | null; endDate: string | null };

export type BudgetView = {
  /**
   * Spent, committed, left and % used are the whole project's; periodSpentCents is the picked period's spending (null when no period is picked).
   * As on Home and the Administration overview (moneyBlock, one calculation): committedCents is what approved ToRs, open contracts and closed
   * events still hold; waitingCents ("Waiting for approval") what ToRs with the funder hold, apart. left = budget − spent − committed − waiting.
   */
  cards: { budgetCents: number; budgetNote: string; spentCents: number; spentPct: number; spentNote: string; committedCents: number; waitingCents: number; waitingSplit: WaitingSplit; leftCents: number; leftPct: number; periodSpentCents?: number | null };
  lines: {
    id: Id;
    number: number;
    name: string;
    /** BU-N12: fees, contingency, expenditure verification or other (the rules on moving money). */
    kind?: BudgetLineKind;
    budgetCents: number;
    spentCents: number;
    /** The picked period's spending on the line; null when no period is picked. */
    periodSpentCents?: number | null;
    /** Committed without what ToRs with the funder hold (that is waitingCents). usePct counts both. */
    committedCents: number;
    waitingCents: number;
    usePct: number;
    leftCents: number;
    /** optional addition */ unitRateCents?: number | null;
    /** Units (B1): the unit ("flight"), planned units and planned value of one unit, as in the contract. */
    unit: string | null;
    plannedUnits: number | null;
    unitValueCents: number | null;
    /** The funder's code ("1.3.3.1") and heading ("Travel"), from the budget import; quiet detail. */
    code?: string | null;
    heading?: string | null;
    /** Units used so far, from the items of spent expenses on the line (see unitUse in the API). Null when the line has no unit. */
    usedUnits: number | null;
    /** Average price of one used unit; null when nothing is used yet. */
    averageUnitCents: number | null;
    /** "Average €640 a flight, planned €600" when the average is above the planned unit value. */
    unitWarning: string | null;
    /** UI-BU1 B5: where the line is expected to end (the opened line's forecast is the same). */
    outlook?: LineOutlook;
    /** Units used and their average over the whole project, whatever period is picked (B3, B4). */
    unitsToDate?: { usedUnits: number | null; averageUnitCents: number | null };
  }[];
  /** UI-BU1 B1: the direct costs, spent and still to come. over: what the planned work costs beyond the budget. */
  outlook?: { budgetCents: number; spentCents: number; committedCents: number; waitingCents: number; stillToPayCents: number; notNeededCents: number; overCents: number };
  /** UI-BU1 B2. Null without a start date or before the project starts. */
  pace?: SpendingPace | null;
  total: { budgetCents: number; spentCents: number; committedCents: number; waitingCents: number; leftCents: number };
  warnings: { text: string; link: Link }[];
  /** Money in from the funder (payment requests); null for partners. */
  moneyIn: {
    receivedCents: number;
    payments: number;
    waitingCents: number;
    waiting: number;
    /** The next payment expected (the oldest request waiting). */
    next: { label: string; amountCents: number; expectedOn: string | null; overdue: boolean } | null;
    path: string;
  } | null;
  /** Suite Budget: the lines under the funder's headings, in order, with subtotals. */
  headings?: { name: string; budgetCents: number; spentCents: number; lineIds: Id[] }[];
  /** The calculated lines: indirect costs (a share of the direct costs) and the reserve. */
  calculated?: { key: "indirect" | "reserve"; label: string; detail: string; cents: number; ofCents: number; status: StatusChip }[];
  /** Contract total, the direct costs (the budget lines) and the indirect share, for the first card. */
  /** null for a partner: the contract card is for the team. */
  contract?: { totalCents: number | null; directCents: number; indirectPct: number | null } | null;
  /** Expenses waiting for approval (the attention strip). */
  toApprove?: { count: number; cents: number; path: string };
  /** The lead may move money between lines (Budget transfer). */
  canMove?: boolean;
};
/** One budget line opened (Suite Budget line panel): its costs, where each came from, and the forecast. */
export type BudgetLineDetail = {
  id: Id;
  title: string;
  /** MD-F5: `units`, what the cost adds to the line's used units (null: it carries none; only on lines with a unit). */
  costs: { id: Id; date: string; title: string; source: string; amountCents: number; counted: boolean; document: boolean; path: string; units?: number | null }[];
  /** MD-F5: the line's unit and used units, from the same costs as spent. */
  units?: { unit: string; usedUnits: number; plannedUnits: number | null; costsWithUnits: number } | null;
  /**
   * MD-F3 (ADR 0001): what makes up the line's committed money: per activity,
   * what its ToRs still reserve and what its open contracts and orders still
   * have to be invoiced; the line counts the larger of the two, never both.
   */
  committed?: {
    totalCents: number;
    rows: { what: string; path: string | null; holds: string; torOpenCents: number; contractsOpenCents: number; committedCents: number; waiting: boolean }[];
  };
  spentCents: number;
  forecast: { cents: number; pct: number; text: string; method?: LineOutlook["method"] } | null;
  /** BU8: why there is no forecast yet (too little data), or null. */
  forecastNote?: string | null;
};
/** Suite Budget Transfer: move money from one line to another. */
export const budgetTransferBody = z.object({ fromLineId: uuid, toLineId: uuid, amountCents: z.number().int().min(1).max(1e13), justification: textOrEmpty(2_000).default("") });
export type BudgetTransferCheck = {
  /** The amount as a share of the budget heading it is taken from, one decimal. */
  pctOfHeading: number;
  limitPct: number;
  needsApproval: boolean;
  /** What happens, in one sentence ("The funder must agree. This moves 15% of a budget heading; …"). */
  message: string;
  /** The lines before and after the move (and the indirect costs, which do not change). */
  after: { line: string; nowCents: number; afterCents: number }[];
  /** Why it cannot be moved ("Printing has only €8,500 left."); empty when it can. */
  problems: string[];
};
export const budgetLinesBody = z.object({
  lines: z
    .array(
      z.object({
        id: uuid.optional(),
        number: z.number().int().min(1).max(999),
        name: text(200, "Budget line"),
        amountCents: centsV3(),
        unitRateCents: centsV3(LIMITS.unitCostCentsMax).nullable(),
        /** Left out = keep what is stored (older clients); null = clear. */
        unit: textOrNull(40, "Unit").optional(),
        plannedUnits: z.number({ invalid_type_error: "Enter a number of units." }).min(0, "Cannot be negative.").max(9_999_999, "The number is too large.").nullable().optional(),
        unitValueCents: centsV3(LIMITS.unitCostCentsMax).nullable().optional(),
        /** #15b: the budget heading the line sits under. Left out = keep what is stored; null = none. */
        heading: textOrNull(120, "Heading").optional(),
        /** BU-N12: the line's kind. Left out = keep what is stored. */
        kind: z.enum(BUDGET_LINE_KINDS).optional(),
      }),
    )
    .max(100),
});

// ---------- budget import (Setup: Import the budget) ----------

/** What a column of the budget file holds. */
export type BudgetImportField = "number" | "code" | "heading" | "name" | "unit" | "plannedUnits" | "unitValue" | "amount";
export type BudgetImportProblem = { level: "error" | "warning"; text: string };
/** AD4: a line as it is in Convenor now, before the import changes it. */
export type BudgetImportLineBefore = { name: string; amountCents: number; unit: string | null; plannedUnits: number | null; unitValueCents: number | null; code: string | null; heading: string | null };
/** AD4: one value an update changes, old → new, as shown ("€1,000.00" → "€1,200.00"). */
export type BudgetImportChange = { field: "amount" | "unit" | "plannedUnits" | "unitValue" | "code" | "heading"; label: string; before: string; after: string };
export type BudgetImportRow = {
  /** The row in the spreadsheet (1 = the first row under the headers). */
  row: number;
  /** create / update a budget line; heading: a heading row, used for the lines under it; skip: not imported (see problems); rule: a calculated line (indirect costs, reserve), read as a rule; total: a total or subtotal of the file, not imported (Convenor adds up the lines; a warning when the file's figure differs). */
  action: "create" | "update" | "heading" | "skip" | "rule" | "total";
  /** The line number in Convenor (from the file, or the next free one). */
  number: number | null;
  code: string | null;
  heading: string | null;
  name: string;
  unit: string | null;
  plannedUnits: number | null;
  unitValueCents: number | null;
  amountCents: number | null;
  problems: BudgetImportProblem[];
  /** Suite Budget Import step 2: a short name suggested for the line (the code stays as detail). */
  suggestedName?: string;
  /** AD4, updates only: the line as it is now, and what the import changes on it (the name is compared on the page, as it can still be edited). */
  before?: BudgetImportLineBefore;
  changes?: BudgetImportChange[];
  /** BU-N10: the heading row a line (or a heading repeated) belongs to; renaming that heading in the preview renames it for its lines. */
  headingRow?: number | null;
  /** BU-N10, heading rows: the same heading as this earlier row, merged with it (the file repeats it). */
  duplicateOf?: number | null;
  /** BU-N9: the sub-heading the line sits under ("1.3 Per diems for missions/travel"). */
  subHeading?: string | null;
};
/** BU-N5: a line of the budget the file does not have; the import leaves it as it is. */
export type BudgetImportNotInFile = { number: number; code: string | null; name: string; amountCents: number };
/** AD4: an amendment of this project the import can be filed under. */
export type BudgetImportAmendment = { id: Id; label: string; reference: string | null };
/** Suite Budget Import step 3: a rule of the budget, found in the file or taken from the project ("Please check"). */
export type BudgetImportRule = { key: "indirect" | "reserve" | "moving"; label: string; pct: number | null; found: boolean; detail: string };
export type BudgetImportPreview = {
  previewId: Id;
  fileName: string;
  /** Which column was read as what (columns not used are left out). */
  columns: { header: string; field: BudgetImportField }[];
  rows: BudgetImportRow[];
  /** totals: the total and subtotal rows of the file, left out. */
  summary: { create: number; update: number; skip: number; totals: number; warnings: number; totalCents: number; /** Updates that change nothing (name included, as in the file). */ unchanged?: number };
  /** Step 3: indirect costs, reserve, moving money. */
  rules?: BudgetImportRule[];
  /** How many headings the file has. */
  headings?: number;
  /**
   * AD4: the contract is signed (a signed contract file, or an amendment the
   * funder agreed) and the project already has budget lines. Then an import
   * that changes the budget is an amendment: the commit asks for its
   * reference (`amendmentNeeded`: the file changes something; a renamed line
   * counts too, which the page adds).
   */
  contract?: { signed: boolean; hadLines: boolean; amendmentNeeded: boolean; amendments: BudgetImportAmendment[] };
  /** BU2 on the budget as it would be after the import: lines that do not add up, a total above the contract. Warnings only. */
  checks?: string[];
  /** BU-N5: the budget's lines that are not in the file (they stay as they are). */
  notInFile?: BudgetImportNotInFile[];
};
export const budgetImportBody = z.object({ fileId: uuid });
/** The commit: the short names checked in step 2 (by row) and the rules confirmed in step 3. */
export const budgetImportCommitBody = z
  .object({
    names: z.record(z.string().regex(/^\d{1,5}$/), text(200, "Name")).optional(),
    rules: z.object({ indirectPct: z.number().min(0).max(100).nullable().optional(), reservePct: z.number().min(0).max(100).nullable().optional(), limitPct: z.number().int().min(0).max(100).optional() }).optional(),
    /** AD4: after signing, the amendment this import belongs to: one of the project's amendments, or the funder's reference typed in. */
    amendmentId: uuid.optional(),
    amendmentReference: textOrEmpty(120, "Amendment reference").optional(),
    /** BU-N10: rows (by row number) the person left out in the preview. */
    exclude: z.array(z.string().regex(/^\d{1,5}$/)).max(2000).optional(),
    /** BU-N10: €0 lines are left out unless named here. */
    include: z.array(z.string().regex(/^\d{1,5}$/)).max(2000).optional(),
    /** BU-N10: a heading renamed in the preview, by its heading row; two headings given the same name are one. */
    headings: z.record(z.string().regex(/^\d{1,5}$/), text(120, "Heading")).optional(),
  })
  .default({});
export type BudgetImportResult = { created: number; updated: number; skipped: number };

/** A unit's plural: "flights", "studies", "people", "trips (return)"; "units" without a unit. */
export function unitPlural(unit: string | null | undefined): string {
  const u = (unit ?? "").trim();
  if (!u) return "units";
  // Workbook 27 Oct: "unit cost (per contract)" reads "units (cost per contract)".
  const perContract = /^unit cost \((per .+)\)$/i.exec(u);
  if (perContract) return `units (cost ${perContract[1]})`;
  // "unit cost (per contract)": the plural goes on the words before the brackets.
  const bracket = u.indexOf(" (");
  const head = bracket > 0 ? u.slice(0, bracket) : u;
  const tail = bracket > 0 ? u.slice(bracket) : "";
  const lower = head.toLowerCase();
  const plural = lower === "person" ? "people" : /(ss|x|ch|sh)$/.test(lower) ? `${head}es` : lower.endsWith("s") ? head : /[^aeiou]y$/.test(lower) ? `${head.slice(0, -1)}ies` : `${head}s`;
  return `${plural}${tail}`;
}
/** "1 flight", "42 flights", "3 people", "1,5 days": a number of units in words. */
export function unitsText(n: number, unit: string): string {
  const u = unit.trim();
  const shown = formatNumber(n);
  if (/^unit cost \((per .+)\)$/i.test(u) && n === 1) return `${shown} unit (cost ${u.slice(u.indexOf("(") + 1, -1)})`;
  if (n === 1 || !u) return `${shown} ${u}`.trim();
  return `${shown} ${unitPlural(u)}`;
}
/** Units used of planned, the unit said once: "2 of 5 flights", "0 of 0 units (cost per contract)". */
export function unitsOfText(used: number, planned: number, unit: string): string {
  const shown = formatNumber(used);
  return `${shown} of ${unitsText(planned, unit)}`;
}
export const expensesQuery = z.object({ tab: z.enum(["all", "to-approve", "claims", "timesheets"]).default("all"), cursor: z.string().max(200).optional() });
export type ExpenseRow = {
  id: Id;
  date: string;
  what: string;
  sub: string;
  from: string;
  line: string | null;
  /** S-091: the Budget page with this line open (`…/budget#line-<id>`), or null without a line. */
  linePath?: string | null;
  activity: string | null;
  amountCents: number;
  status: StatusChip;
  path: string;
};
/** optional addition: GET /p/:projectId/expenses/new-options (the New Expense pickers). */
export type ExpenseOptions = {
  budgetLines: { id: Id; label: string }[];
  experts: { engagementId: Id; name: string; category: string }[];
  /** BU7: per activity, its own travel or per-diem line (from its ToR cost lines, its matrix cost lines or the lines it uses first), or null. */
  activities: { id: Id; code: string; title: string; startDate: string | null; endDate: string | null; claimLineId?: Id | null }[];
  /** BU7: the line a travel claim goes on by default (a travel or per-diem line), or null. */
  claimLineId?: Id | null;
  /** BU8 (workbook 27 Oct): the project team's full names, offered for a staff member's claim. */
  staff?: string[];
};
export type ExpenseList = { tab: string; counts: Record<"all" | "to-approve" | "claims" | "timesheets", number>; rows: ExpenseRow[]; nextCursor: string | null };
export const expenseItem = z.object({
  item: text(200, "Item"),
  quantity: z.number().min(0).max(10_000),
  rateCents: centsV3(LIMITS.unitCostCentsMax),
  receiptFileId: uuid.nullable(),
});
export const expenseBody = z.object({
  kind: z.enum(["invoice", "claim", "other"]),
  title: text(200, "Title"),
  date: isoDate,
  claimant: z.object({ type: z.enum(CLAIMANT_TYPES), refId: uuid.nullable(), name: textOrNull(LIMITS.userName) }),
  supplier: textOrNull(200),
  invoiceNumber: textOrNull(60),
  activityId: uuid.nullable(),
  travelFrom: isoDate.nullable(),
  travelTo: isoDate.nullable(),
  destinationCountry: country.nullable(),
  budgetLineId: uuid,
  items: z.array(expenseItem).max(100),
  amountCents: centsV3().nullable(),
  /** BU-N4: receipts of the cost as a whole (an invoice entered by its amount, without items). */
  receiptFileIds: z.array(uuid).max(20).optional(),
  /**
   * MD-F6 (ADR 0001): a cost of a supplier with an open contract or order on
   * the same activity: the contract or order it pays (the commitment drops by
   * the same amount), or `separate: true` when it is a cost of its own.
   * Without either, the API answers 409 PAYS_CONTRACT with the open ones.
   */
  paysCommitmentId: uuid.nullable().optional(),
  separateFromContract: z.boolean().optional(),
});
export type ExpenseBody = z.infer<typeof expenseBody>;
/** MD-F6: the 409 PAYS_CONTRACT answer's details: the supplier's open contracts and orders on the activity. */
export type OpenContractsOfSupplier = { contracts: { id: Id; label: string; openCents: number }[] };
/** BU4: a sent-back cost as entered, for the edit form (PUT /expenses/:id takes an `expenseBody`). */
export type ExpenseEditValues = {
  kind: "invoice" | "claim" | "other";
  title: string;
  date: string;
  claimant: { type: (typeof CLAIMANT_TYPES)[number]; refId: Id | null; name: string | null };
  supplier: string | null;
  invoiceNumber: string | null;
  activityId: Id | null;
  travelFrom: string | null;
  travelTo: string | null;
  destinationCountry: string | null;
  budgetLineId: Id | null;
  items: { item: string; quantity: number; rateCents: number }[];
  amountCents: number;
};
export type ExpenseDetail = {
  id: Id;
  title: string;
  status: StatusChip;
  line: string;
  items: { id: Id; item: string; quantity: number; rateCents: number; totalCents: number; check: StatusChip; excluded: boolean }[];
  checks: { label: string; ok: boolean }[];
  approveAllCents: number;
  /** S-095: the amount approved, once approved; below `approveAllCents` when items were left out. Null before approval. */
  approvedCents?: number | null;
  /**
   * The offer to approve without some items: those with a receipt missing, and (IX-F7) the per diems
   * another claim already holds. `blocked`: why even this cannot be approved (the ToR), else null.
   */
  approvePartial: { cents: number; label: string; excludeItemIds: Id[]; blocked?: string | null } | null;
  /** IX-F7: what happened to this cost (submitted, sent back with the note, sent again, approved), newest first. */
  history?: HistoryEntry[];
  receipts: FileRef[];
  missingReceiptItemIds: Id[];
  noteToClaimant: string | null;
  linked: Link[];
  /** BU8: who claims, in full: "Jean Morel (NKE)", the supplier's name, the staff member. */
  claimant: string;
  /** BU-N4: a supplier's invoice: the note sent back is not addressed to the supplier. */
  bySupplier?: boolean;
  /** decide: approve or send back. amend (BU4): edit, resubmit or withdraw a sent-back cost (the submitter or the project team). */
  can: { decide: boolean; amend: boolean };
  /** BU4: what the edit form starts from, when the cost can be amended; else null. */
  edit: ExpenseEditValues | null;
  /** BU3: why the cost cannot be approved yet (its activity's ToR is not approved), or null. Sending it back still works. */
  approveBlocked?: string | null;
  /** IX-F10: what the files of the cost are called: "Invoice" for an expert's fee invoice (and the fee costs it settles), else "Receipts". */
  filesTitle?: string;
};
/** IX-F6 (feedback round 3): `note` goes to the claimant with a partial approval (what was left out and why). */
export const approveExpenseBody = z.object({ excludeItemIds: z.array(uuid).max(100), note: z.string().trim().max(4_000, "Note must be 4000 characters or fewer.").optional() });
export const sendBackBody = z.object({ note: text(4_000, "Note") });
/** Without itemId (BU-N4): a receipt of the cost as a whole, for a cost without items. */
export const receiptBody = z.object({ itemId: uuid.optional(), fileId: uuid });
export const paidBody = z.object({ paidOn: isoDate });
/** BU7: with `budgetLineId`, the line's own rate when no table has the country. */
export const perDiemLookupQuery = z.object({ country, budgetLineId: uuid.optional() });
export type PerDiemLookup = { rateCents: number | null; source: string };
/**
 * A payment request to the funder (table invoice): pre-financing, interim,
 * final (balance) or other. Draft → sent (waiting) → received or rejected.
 * Received requests are the project's money in (Budget, financial report).
 */
export type InvoiceRow = {
  id: Id;
  number: string;
  kind: string;
  /** The amount asked for. */
  amountCents: number;
  /** "70% of the second tranche", "Costs incurred in period 2". */
  basis: string;
  /** The report it goes with. */
  report: { title: string; path: string } | null;
  sentAt: string | null;
  /** When the funder is expected to pay (sent date + the contract's payment days, unless set). */
  expectedOn: string | null;
  /** Sent, not received, and the expected date has passed. */
  overdue: boolean;
  status: StatusChip;
  paidAt: string | null;
  /** What the funder paid (null until received). */
  receivedCents: number | null;
  rejectedNote: string | null;
  /** The request letter (Word) */
  path: string;
  /** Documents sent with it (expenditure verification, bank details form). */
  documents: FileRef[];
};
export type InvoiceList = {
  cards: { invoicedCents: number; count: number; paidCents: number; waitingCents: number; waitingDays: number | null; next: string | null; overdue: number };
  rows: InvoiceRow[];
  canCreate: boolean;
};
export type InvoiceDraft = {
  kinds: { value: string; label: string }[];
  reports: { id: Id; label: string; approved: boolean }[];
  lines: { budgetLine: string; eligibleCents: number }[];
  totalEligibleCents: number;
  prefinancingToClearCents: number;
  /** Pre-financing not cleared yet by the requests sent so far. */
  prefinancingLeftCents: number;
  /** Costs on no budget line are in the total: say so before the request is sent. */
  warning: string | null;
  /** The suggested amount; 0 when there is nothing to suggest from (then the team types the amount). */
  requestedCents: number;
  explanation: string;
  /** "Costs incurred from 1 Mar 2026 to 28 Feb 2027", or "" */
  basis: string;
  /** Payments the funder made so far. */
  received: { number: string; kind: string; cents: number; on: string }[];
  /** Suite Payment Request: the contract's payments not asked for yet, each with the report it goes with. */
  schedule?: (PaymentScheduleEntry & { reportId: Id | null })[];
  /** What the funder receives with this request, each with where it stands. */
  receives?: { label: string; status: StatusChip }[];
};
/** One payment of the contract's schedule: "Payment 3 of 4 · after interim report 2". */
export type PaymentScheduleEntry = { id: Id; number: number; kind: (typeof INVOICE_KINDS)[number]; amountCents: number; afterReportNo: number | null; label: string; status: StatusChip; requested: boolean };
export type PaymentScheduleView = { entries: PaymentScheduleEntry[]; canEdit: boolean };
export const paymentScheduleBody = z.object({
  entries: z
    .array(z.object({ id: uuid.optional(), kind: z.enum(INVOICE_KINDS), amountCents: centsV3(), afterReportNo: z.number().int().min(1).max(40).nullable() }))
    .max(40),
});
export const newInvoiceBody = z.object({
  kind: z.enum(INVOICE_KINDS),
  reportId: uuid.nullable(),
  periodStart: isoDate,
  periodEnd: isoDate,
  supportingFileIds: z.array(uuid).max(30),
  /** The amount to ask for; left out or null = the suggested amount. */
  requestedCents: centsV3().nullable().optional(),
  /** The pre-financing this request clears; left out or null = the suggestion. */
  prefinancingClearedCents: centsV3().nullable().optional(),
  basis: z.string().trim().max(300, "Keep it under 300 characters.").optional(),
  /** The payment of the contract's schedule this request asks for. */
  scheduleId: uuid.nullable().optional(),
});
/** expectedOn: when the funder should pay; left out = the sent date plus the contract's payment days. */
export const sentOnBody = z.object({ sentOn: isoDate, expectedOn: isoDate.nullable().optional() });
/** The funder paid: on what date, and how much (left out = the amount requested). */
export const invoicePaidBody = z.object({ paidOn: isoDate, receivedCents: centsV3().nullable().optional() });
export const invoiceRejectedBody = z.object({ rejectedOn: isoDate, note: z.string().trim().max(2_000, "Keep it under 2,000 characters.").default("") });

// ---------- 11.5 amendments ----------

const justification = text(10_000, "Justification");
export const amendmentBody = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("budget"), fromLineId: uuid, toLineId: uuid, amountCents: centsV3(), justification, effectiveDate: isoDate }),
  z.object({ kind: z.literal("dates"), newEndDate: isoDate, justification, effectiveDate: isoDate }),
  z.object({ kind: z.literal("experts"), leavingEngagementId: uuid.nullable(), proposedExpertId: uuid, days: days(), justification, effectiveDate: isoDate }),
  z.object({
    kind: z.literal("activities"),
    changes: z
      .array(z.object({ action: z.enum(["add", "drop", "change"]), activityId: uuid.nullable(), code: textOrEmpty(LIMITS.activityCodeV3), title: textOrEmpty(LIMITS.torTitle) }))
      .min(1)
      .max(50),
    justification,
    effectiveDate: isoDate,
  }),
]);
export type AmendmentBody = z.infer<typeof amendmentBody>;
export const AMENDMENT_KIND_VALUES = AMENDMENT_KINDS;
export type AmendmentRow = {
  id: Id;
  request: string;
  summary: string;
  kind: string;
  sentAt: string | null;
  /** The day it was decided, else sent (null for a draft). */
  at?: string | null;
  status: StatusChip;
  path: string;
  /** Suite Approvals: experts = an expert change; budget, dates, activities = other requests. */
  kindKey?: "budget" | "dates" | "experts" | "activities";
  /** The money it moves (budget changes). */
  amountCents?: number | null;
  /** The funder's conditions or note on the answer. */
  funderNote?: string | null;
  /** AP3: a notification letter goes Draft, Sent, Done and never waits for the funder. */
  via?: "addendum" | "notification_letter";
};
export type AmendmentsView = { rows: AmendmentRow[]; deadlines: string[]; canCreate: boolean };
export type AmendmentOptions = {
  budgetLines: { id: Id; label: string; leftCents: number; amountCents: number }[];
  /** expertId: the pool record, so Proposed can leave out the person leaving (EX6). */
  experts: { engagementId: Id; expertId?: Id; name: string; category: string }[];
  pool: { expertId: Id; name: string }[];
  activities: { id: Id; code: string; title: string }[];
  earliestEffective: string;
  lastAllowedSend: string;
  /** AP3: expert changes need no approval on this project, so they go by notification letter (no 30-day rule). */
  expertsByLetter?: boolean;
};
export type AmendmentDetail = {
  id: Id;
  title: string;
  status: StatusChip;
  line: string;
  steps: Steps;
  via: "addendum" | "notification_letter";
  viaNote: string | null;
  change: { label: string; value: string; path: string | null }[];
  attached: { label: string; file: FileRef | null; generated: boolean; status: StatusChip }[];
  reminderMailto: string | null;
  history: HistoryEntry[];
  /** done: a sent notification letter can be marked done (AP3). */
  can: { send: boolean; decide: boolean; done?: boolean };
  /** optional addition: the requested effective date (ISO) */
  effectiveDate?: string | null;
  /**
   * Once agreed: what changed in the contract, from the copies kept before and
   * after (lines like "Budget line 2 Flights: €12,000 → €14,500 (+€2,500)"),
   * or why there is no copy. Null while not agreed.
   */
  recorded?: { changes: string[]; note: string | null } | null;
};

// ---------- compare contract versions (review V1 "Instead") ----------

/**
 * A copy of everything an amendment can change, kept on the amendment when it
 * is agreed (amendment.before / amendment.after). Amounts in cents.
 */
export type ContractCopy = {
  v: 1;
  takenAt: string;
  contract: { startDate: string | null; endDate: string | null; envelopeCents: number | null };
  experts: { id: Id; name: string; role: string; days: number | null }[];
  budget: { id: Id; number: number; code: string | null; name: string; amountCents: number }[];
  results: { id: Id; code: string; label: string }[];
  indicators: { id: Id; code: string; label: string; unit: string; baseline: number | null; target: number | null; targetDate: string | null }[];
  linesOfWork: { id: Id; name: string; plannedCount: number | null }[];
  activities: { id: Id; code: string; title: string }[];
};
export type ContractVersionPoint = { key: string; label: string; available: boolean; note: string | null };
export type ContractChange = {
  /** "Budget line 2 Flights" */
  what: string;
  /** "Budget", "End date", "Target"; "" for a whole item added or removed */
  field: string;
  kind: "changed" | "added" | "removed";
  from: string;
  to: string;
  /** "+€2,500" for money */
  delta: string | null;
  /** "Amendment 2" when an agreed amendment made the change, "Edited directly" when none did, null when not known */
  by: string | null;
  /** The whole change in one plain sentence */
  text: string;
};
export type ContractCompareGroup = { key: "contract" | "budget" | "results" | "work"; label: string; changes: ContractChange[] };
export type ContractCompareView = {
  points: ContractVersionPoint[];
  from: string;
  to: string;
  fromLabel: string;
  toLabel: string;
  /** Why the two points cannot be compared (a copy was not recorded); groups are then empty. */
  unavailable: string | null;
  groups: ContractCompareGroup[];
};
export const contractCompareQuery = z.object({ from: z.string().max(60).optional(), to: z.string().max(60).optional() });

/** "Who changed what, when" for one budget line, result or indicator (from the activity log). */
export const HISTORY_ENTITY_TYPES = ["budget_line", "result", "indicator"] as const;
export type HistoryEntityType = (typeof HISTORY_ENTITY_TYPES)[number];
export type EntityHistoryView = { kind: string; title: string; backPath: string; rows: HistoryEntry[] };
export const amendmentDecisionBody = z.object({
  decision: z.enum(["approved", "approved_with_conditions", "refused"]),
  effectiveDate: isoDate,
  reference: textOrEmpty(120),
  fileId: uuid.nullable(),
  conditions: textOrEmpty(10_000),
});
export type DecisionPreview = { whatHappens: string[] };

// ---------- "How this project works" and the Contract tab (Suite Setup Contract, Suite Contract) ----------

/** The four questions that set up Administration and reporting for a project. */
export type HowItWorks = {
  fundingType: FundingType;
  /** How a service contract is paid (fee-based or global price); null for a grant. */
  serviceKind?: "service_fee_based" | "service_global_price" | null;
  /** The results framework's level names, top first (2 to 5). */
  levelNames: string[];
  approval: {
    /** Terms of Reference for each activity: work and costs wait until approved (the project default, D6). */
    tors: boolean;
    expertChanges: boolean;
    /** Moving money between budget lines above `limitPct` % of a heading. */
    budgetMoves: boolean;
    limitPct: number;
    /** None of the three: "Nothing: we report after the fact" (hides the Approvals tab). */
    nothing: boolean;
  };
  reporting: { everyMonths: 0 | 6 | 12; dueDays: number; format: ReportFormat };
  linkFunderIndicators: boolean;
  indirectPct: number | null;
  /** The reserve for unforeseen costs, % of direct costs (null = none). */
  reservePct?: number | null;
  cofinancingPct: number | null;
  /** UI-AN4 (retest 30 Oct): the project's target share of women at events (%); null = none set. */
  womenTargetPct?: number | null;
  /** Show the Approvals tab: something needs approval, or an activity or ToR still waits for one. */
  showApprovals: boolean;
  canEdit: boolean;
  /** The contract profile (compliance proposal, section 1): every contract rule in one shape. */
  profile?: ContractProfile;
  /** The preset the project follows; null = set by hand, or from before presets. */
  preset?: ContractPreset | null;
  /** Keys of the settings that differ from that preset (`profileChanges` keys, e.g. "indirectPct"). */
  changedFromPreset?: string[];
};
export const howItWorksBody = z.object({
  fundingType: z.enum(FUNDING_TYPES),
  /** For a service contract: how it is paid. Left out: the kind it had (fee-based for a former grant). */
  serviceKind: z.enum(["service_fee_based", "service_global_price"]).optional(),
  levelNames: z.array(text(40, "Level name")).min(2).max(5),
  approval: z.object({ tors: z.boolean(), expertChanges: z.boolean(), budgetMoves: z.boolean(), limitPct: z.number().int().min(0).max(100) }),
  reporting: z.object({ everyMonths: z.union([z.literal(0), z.literal(6), z.literal(12)]), dueDays: z.number().int().min(1).max(365), format: z.enum(REPORT_FORMATS), finalDueMonths: z.number().int().min(1).max(12).optional() }),
  linkFunderIndicators: z.boolean(),
  indirectPct: z.number().min(0).max(100).nullable(),
  reservePct: z.number().min(0).max(100).nullable().optional(),
  cofinancingPct: z.number().min(0).max(100).nullable(),
  // The contract profile; a field left out keeps its saved value.
  preset: z.enum(CONTRACT_PRESETS_IDS).nullable().optional(),
  workingDays: z.object({ hoursPerDay: z.number().min(1).max(24).nullable(), halfDays: z.boolean(), invoiceIn: z.enum(INVOICE_UNITS).nullable() }).optional(),
  currencyRule: z.enum(CURRENCY_RULES).nullable().optional(),
  retentionYears: z.number().int().min(1).max(30).nullable().optional(),
  dataProtection: z.object({ regime: z.enum(DP_REGIMES).nullable(), controller: textOrEmpty(200, "Data controller"), breachNoticeHours: z.number().int().min(1).max(720).nullable() }).optional(),
});
export type HowItWorksBody = z.infer<typeof howItWorksBody>;

/** The Contract tab: the contract details plus how the project works, its versions and its obligations. */
export type ContractTabView = {
  howItWorks: HowItWorks;
  /** "Version 2", "after amendment 1, 14 Jun 2026" */
  versionInForce: { label: string; sub: string };
  /** "Every 6 months", "next report due 14 Oct" */
  reporting: { label: string; sub: string };
  /** What the setup reads as, line by line ("Funder approval": "Needed for each ToR and each expert change"). */
  setupLines: { label: string; value: string }[];
  /** Newest first: the version, when, what changed, In force or Replaced. */
  versions: { version: number; date: string | null; text: string; status: StatusChip; path: string | null }[];
  obligations: { label: string; detail: string }[];
  /** CO1: the one source for the contract manager's name: who was invited to the contract manager view (not revoked). */
  contractManagers?: { name: string; email: string; status: "invited" | "active" }[];
  /** UI-CO1: false when the contact at the funder is the contract manager already listed. */
  funderContactShown?: boolean;
};
/** POST /tors/:torId/reminder (AP1): who the reminder went to, and the ToR's reminder line. */
export type TorReminderSent = { to: string[]; reminder: { sentOn: string; mail: MailStatus | null } };

// ---------- 11.6 expert days, documents, contract, close ----------

export type ExpertDaysView = {
  cards: { contracted: number; used: number; usedPct: number; planned: number; left: number };
  rows: { name: string; path: string; category: string; budget: number; used: number; planned: number; left: number }[];
};
export const documentsQuery = z.object({ type: z.enum(DOCUMENT_TYPES).optional(), cursor: z.string().max(200).optional() });
export type DocumentRow = { id: Id; file: FileRef; type: string; linkedTo: Link | null; addedAt: string; addedBy: string };
export type DocumentsPage = Paged<DocumentRow>;
export type DocumentLinkOptions = { targets: { value: string; label: string; group: string }[] };
export const newDocumentBody = z.object({
  fileId: uuid,
  type: z.enum(DOCUMENT_TYPES),
  linkedTo: z
    .string()
    .regex(/^[a-z_]{2,40}:[0-9a-f-]{36}$/, "Choose what the document belongs to.")
    .nullable(),
  note: textOrEmpty(1_000),
  role: z.string().max(40).optional(),
});
export type NewDocumentResult = { id: Id; returnTo: string | null };
export type CloseChecklist = {
  endDate: string | null;
  archiveUntil: string | null;
  items: {
    key: "final_report" | "verification" | "final_invoice" | "handover" | "open_items" | "archive";
    label: string;
    detail: string;
    status: StatusChip;
    link: Link | null;
  }[];
  retentionChoice: string;
  canClose: boolean;
  closeReason: string | null;
  archivedAt: string | null;
};
export const closeSettingsBody = z.object({ retentionChoice: z.enum(RETENTION_CHOICES), handoverFileIds: z.array(uuid).max(50) });
export const reopenBody = z.object({ note: text(LIMITS.transitionNote, "Note") });
